import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthProvider'
import type { Tenant } from '../types'

interface TenantContextValue {
  tenants: Tenant[]
  tenant?: Tenant
  tenantId?: string
  /** A própria organização de quem está logado (a agência, no caso de uma revenda) -- usada pra branding, não é a organização selecionada no seletor. */
  ownTenant?: Tenant
  loading: boolean
  setTenantId: (id: string) => void
}
const TenantContext = createContext<TenantContextValue | null>(null)

export function TenantProvider({ children }: PropsWithChildren) {
  const auth = useAuth()
  const profile = auth.profile
  const claimTenantId = profile.tenantId ?? undefined
  // Admin da plataforma lista todas as organizações; uma agência (revenda) lista só as que ela mesma criou -- o backend já filtra isso automaticamente pra quem não é admin.
  const canListMany = auth.isAdmin || auth.isReseller
  const query = useQuery({ queryKey: ['tenants'], queryFn: () => api.tenants(auth.token), enabled: auth.authenticated && canListMany })
  const mineQuery = useQuery({ queryKey: ['my-tenants'], queryFn: () => api.myTenants(auth.token), enabled: auth.authenticated && !canListMany })
  // Quem não lista muitos tenants (membro comum ou revenda) busca o próprio registro
  // completo -- sem isso, `tenant` ficaria só com o placeholder abaixo (active/planCode
  // fixos, nunca refletindo o que está de verdade no banco), e qualquer aviso que dependa
  // desses campos (ex.: "conta aguardando ativação", validade do plano FREE) nunca apareceria
  // de verdade pro cliente final, só pra quem lista muitos tenants (admin/revenda).
  const ownQuery = useQuery({
    queryKey: ['own-tenant', claimTenantId],
    queryFn: () => api.tenant(claimTenantId!, auth.token),
    enabled: auth.authenticated && !canListMany && Boolean(claimTenantId),
  })
  const tenants = useMemo(() => {
    if (canListMany) return query.data ?? []
    if (!claimTenantId) return []
    // Placeholder só até ownQuery carregar (evita a tela piscar vazia) -- os campos
    // reais (active, planCode, planValidUntil etc.) só existem depois que a busca acima
    // resolve, então nunca dependa desses valores fixos pra decisões de negócio no render.
    return mineQuery.data?.length ? mineQuery.data : [ownQuery.data ?? { id: claimTenantId, name: profile.tenantName ?? 'Minha organização', slug: '', active: true, createdAt: '', logoDataUri: null, parentAgencyId: null, planCode: 'ESSENCIAL', attachmentLimitsOverride: { maxAttachments: null, maxAttachmentBytes: null, maxTotalBytes: null, maxAttachmentStorageBytes: null }, profileCode: null, document: null, activatedAt: null, planValidUntil: null }]
  }, [canListMany, claimTenantId, mineQuery.data, profile.tenantName, query.data, ownQuery.data])
  const [tenantId, setTenantIdState] = useState(() => (auth.isReseller ? undefined : claimTenantId) ?? localStorage.getItem('emailserver.tenantId') ?? undefined)
  const effectiveTenantId = tenantId && tenants.some((item) => item.id === tenantId) ? tenantId : tenants[0]?.id

  const setTenantId = (id: string) => { localStorage.setItem('emailserver.tenantId', id); setTenantIdState(id) }
  const value = useMemo(() => ({
    tenants,
    tenant: tenants.find((item) => item.id === effectiveTenantId),
    tenantId: effectiveTenantId,
    ownTenant: auth.isReseller ? ownQuery.data : undefined,
    loading: query.isLoading || mineQuery.isLoading || ownQuery.isLoading,
    setTenantId,
  }), [auth.isReseller, effectiveTenantId, mineQuery.isLoading, ownQuery.data, ownQuery.isLoading, query.isLoading, tenants])
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (!context) throw new Error('useTenant deve estar dentro de TenantProvider')
  return context
}
